import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { isMac, isSafari } from '@/utils/pwa';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isStandalone: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
}

export function usePWA() {
  const [installState, setInstallState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    isIOS: false,
    isStandalone: false,
    installPrompt: null
  });

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newServiceWorker, setNewServiceWorker] = useState<ServiceWorker | null>(null);

  // Detect iOS
  const isIOS = useCallback(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
  }, []);

  // Detect standalone mode
  const isStandalone = useCallback(() => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as unknown as { standalone?: boolean }).standalone ||
           document.referrer.includes('android-app://');
  }, []);

  // Check if app is installable
  useEffect(() => {
    const ios = isIOS();
    const standalone = isStandalone();
    
    // Better detection for installed PWA on desktop and mobile
     const isInstalledPWA = standalone || 
       (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
       (navigator as unknown as { standalone?: boolean }).standalone === true ||
       document.referrer.includes('android-app://') ||
       window.location.search.includes('utm_source=pwa') ||
       (ios && (navigator as unknown as { standalone?: boolean }).standalone);
    
    setInstallState(prev => ({
      ...prev,
      isIOS: ios,
      isStandalone: standalone,
      isInstalled: isInstalledPWA
    }));

    // Handle beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      
      setInstallState(prev => ({
        ...prev,
        isInstallable: true,
        installPrompt: promptEvent
      }));

      console.log('💡 PWA install prompt available');
    };

    // Handle app installed event
    const handleAppInstalled = () => {
      setInstallState(prev => ({
        ...prev,
        isInstallable: false,
        isInstalled: true,
        installPrompt: null
      }));

      toast.success('App instalado com sucesso! 🎉');
      console.log('✅ PWA installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isIOS, isStandalone]);

  // Service Worker update detection
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const checkForUpdates = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (!registration) return;

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('🔄 New service worker available');
                setNewServiceWorker(newWorker);
                setUpdateAvailable(true);
              }
            });
          });

          // Check if there's a waiting service worker
          if (registration.waiting) {
            setNewServiceWorker(registration.waiting);
            setUpdateAvailable(true);
          }

        } catch (error) {
          console.error('Error checking for updates:', error);
        }
      };

      checkForUpdates();

      // Listen for service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_UPDATE_AVAILABLE') {
          setUpdateAvailable(true);
        }
      });
    }
  }, []);

  // Install the PWA
  const installPWA = useCallback(async () => {
    if (!installState.installPrompt) {
      console.log('❌ No install prompt available');
      return false;
    }

    try {
      console.log('🚀 Triggering PWA install...');
      await installState.installPrompt.prompt();
      
      const choice = await installState.installPrompt.userChoice;
      console.log('👤 User choice:', choice.outcome);

      if (choice.outcome === 'accepted') {
        toast.success('Instalação iniciada! 📱');
      } else {
        toast.info('Instalação cancelada');
      }

      setInstallState(prev => ({
        ...prev,
        installPrompt: null
      }));

      return choice.outcome === 'accepted';
    } catch (error) {
      console.error('Error installing PWA:', error);
      toast.error('Erro ao instalar o app');
      return false;
    }
  }, [installState.installPrompt]);

  // Update the service worker
  const updateServiceWorker = useCallback(() => {
    if (!newServiceWorker) {
      console.warn('No new service worker available');
      return;
    }

    console.log('⚡ Updating service worker...');
    
    try {
      newServiceWorker.postMessage({ type: 'SKIP_WAITING' });

      // Listen for the controlling service worker to change
      const handleControllerChange = () => {
        console.log('🔄 Service worker updated, reloading...');
        window.location.reload();
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
      
      // Timeout de segurança - se não houver controllerchange em 8 segundos
      setTimeout(() => {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        console.log('⏰ Service worker update timeout, forcing reload...');
        window.location.reload();
      }, 8000);

      setUpdateAvailable(false);
      setNewServiceWorker(null);
      
    } catch (error) {
      console.error('Error updating service worker:', error);
      // Em caso de erro, força o reload após um delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }, [newServiceWorker]);

  return {
    ...installState,
    updateAvailable,
    installPWA,
    updateServiceWorker,
    canInstall: (installState.isInstallable || installState.isIOS || (isMac() && isSafari())) && !installState.isInstalled && !installState.isStandalone
  };
}